import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { payrollAPI, employeeAPI, salaryAdvanceAPI, cloudinaryService } from '../../services';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import HoursDetailModal from './HoursDetailModal';
import PointsDetailModal from './PointsDetailModal';
import ManualPointModal from './ManualPointModal';
import {
  HOURLY_RATE_TIERS,
  isHourlyRateTier,
  formatHourlyRateTier,
} from '../../utils/hourlyRateTiers';

const formatCurrency = (value) => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(Number(value));
};

const formatMonth = (month) => {
  if (!month) return '-';
  try {
    const [year, monthNum] = month.split('-');
    return `Tháng ${monthNum}/${year}`;
  } catch {
    return month;
  }
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'dd/MM/yyyy HH:mm');
  } catch {
    return value;
  }
};

export default function PayrollDetailModal({ show, onHide, payrollId, onRefresh, currentUserId }) {
  const [payroll, setPayroll] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [showManualPointModal, setShowManualPointModal] = useState(false);
  const [showEditHourlyRateModal, setShowEditHourlyRateModal] = useState(false);
  const [newHourlyRate, setNewHourlyRate] = useState(0);
  const [updatingHourlyRate, setUpdatingHourlyRate] = useState(false);
  // #175506 — ứng lương của đúng kỳ lương này
  const [advances, setAdvances] = useState([]);
  const [showAdvanceForm, setShowAdvanceForm] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [advanceNote, setAdvanceNote] = useState('');
  const [savingAdvance, setSavingAdvance] = useState(false);
  // #175506 — hạn mức ứng lương của kỳ (trần 100% lương thực nhận, đọc từ máy chủ)
  const [advanceQuota, setAdvanceQuota] = useState(null);
  // #175506 — ảnh chuyển khoản (KHÔNG bắt buộc: thiếu ảnh chỉ cảnh báo, vẫn chốt trả được)
  const [uploadingEvidence, setUploadingEvidence] = useState(false);

  useEffect(() => {
    if (show && payrollId) {
      fetchPayroll();
    } else {
      setPayroll(null);
      setAdvances([]);
      setAdvanceQuota(null);
      setShowAdvanceForm(false);
    }
  }, [show, payrollId]);

  const fetchPayroll = async () => {
    try {
      setLoading(true);
      const response = await payrollAPI.getPayrollById(payrollId);
      setPayroll(response);
      await fetchAdvances(response);
    } catch (error) {
      console.error('Cannot fetch payroll', error);
      toast.error(error?.message || 'Không tải được thông tin bảng lương');
    } finally {
      setLoading(false);
    }
  };

  // #175506 — danh sách khoản ứng của ĐÚNG (nhân viên, kỳ lương) đang xem.
  const fetchAdvances = async (target) => {
    if (!target?.employee_id || !target?.month) return;
    try {
      const list = await salaryAdvanceAPI.list({
        employee_id: target.employee_id,
        month: target.month,
      });
      setAdvances(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Cannot fetch salary advances', error);
      setAdvances([]);
    }

    // Hạn mức lấy THẲNG từ máy chủ (cùng một phép tính với chỗ chặn) để màn hình và máy chủ
    // không bao giờ nói hai con số khác nhau.
    try {
      setAdvanceQuota(
        await salaryAdvanceAPI.quota({
          employee_id: target.employee_id,
          month: target.month,
        }),
      );
    } catch (error) {
      console.error('Cannot fetch salary advance quota', error);
      setAdvanceQuota(null);
    }
  };

  const handleCreateAdvance = async () => {
    const amount = Number(advanceAmount);
    if (!Number.isInteger(amount) || amount <= 0) {
      toast.error('Số tiền ứng phải là số nguyên lớn hơn 0');
      return;
    }

    try {
      setSavingAdvance(true);
      const created = await salaryAdvanceAPI.create({
        employee_id: payroll.employee_id,
        month: payroll.month,
        amount,
        ...(advanceNote.trim() ? { note: advanceNote.trim() } : {}),
      });
      if (created?.warning) toast.warning(created.warning);
      toast.success('Đã ghi nhận khoản ứng lương');

      // Tính lại ngay để dòng "Đã ứng lương" và lương thực nhận khớp với khoản vừa thêm.
      await recalcAfterAdvanceChange();
      setAdvanceAmount('');
      setAdvanceNote('');
      setShowAdvanceForm(false);
    } catch (error) {
      console.error('Cannot create salary advance', error);
      toast.error(error?.message || 'Không thể ghi nhận khoản ứng lương');
    } finally {
      setSavingAdvance(false);
    }
  };

  const handleDeleteAdvance = async (advanceId) => {
    if (!window.confirm('Xoá khoản ứng lương này?')) return;
    try {
      setSavingAdvance(true);
      await salaryAdvanceAPI.remove(advanceId);
      toast.success('Đã xoá khoản ứng lương');
      await recalcAfterAdvanceChange();
    } catch (error) {
      console.error('Cannot delete salary advance', error);
      toast.error(error?.message || 'Không thể xoá khoản ứng lương');
    } finally {
      setSavingAdvance(false);
    }
  };

  const recalcAfterAdvanceChange = async () => {
    try {
      await payrollAPI.calculate({
        employee_id: payroll.employee_id,
        month: payroll.month,
      });
    } catch (calcError) {
      console.error('Error recalculating payroll after advance change:', calcError);
      toast.warning('Đã lưu khoản ứng nhưng chưa tính lại được bảng lương — bấm "Tính lương tất cả" để cập nhật.');
    }
    await fetchPayroll();
    if (onRefresh) onRefresh();
  };

  const handleUploadEvidence = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setUploadingEvidence(true);
      const result = await cloudinaryService.uploadImage(file, {
        folder: 'payroll/payment_evidence',
      });
      if (!result?.secure_url) throw new Error('Upload không trả về đường dẫn ảnh');
      await payrollAPI.updatePaymentEvidence(payrollId, [
        ...(payroll.payment_evidence_urls || []),
        result.secure_url,
      ]);
      toast.success('Đã cập nhật ảnh chuyển khoản');
      await fetchPayroll();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Cannot upload payment evidence', error);
      toast.error(error?.message || 'Không thể tải ảnh chuyển khoản');
    } finally {
      setUploadingEvidence(false);
    }
  };

  const handleRemoveEvidence = async (url) => {
    if (!window.confirm('Xoá ảnh chuyển khoản này?')) return;
    try {
      setUploadingEvidence(true);
      await payrollAPI.updatePaymentEvidence(
        payrollId,
        (payroll.payment_evidence_urls || []).filter((u) => u !== url),
      );
      await fetchPayroll();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Cannot remove payment evidence', error);
      toast.error(error?.message || 'Không thể xoá ảnh chuyển khoản');
    } finally {
      setUploadingEvidence(false);
    }
  };

  const handleApprove = async () => {
    if (!currentUserId) {
      toast.error('Không xác định được người duyệt');
      return;
    }

    try {
      setActionLoading(true);
      await payrollAPI.approve(payrollId, { current_user_id: currentUserId });
      toast.success('Duyệt bảng lương thành công');
      await fetchPayroll();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Cannot approve payroll', error);
      toast.error(error?.message || 'Không thể duyệt bảng lương');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!currentUserId) {
      toast.error('Không xác định được người đánh dấu');
      return;
    }

    try {
      setActionLoading(true);
      const kq = await payrollAPI.markPaid(payrollId, { current_user_id: currentUserId });
      toast.success('Đánh dấu đã thanh toán thành công');
      // #175506 — ảnh chuyển khoản không bắt buộc: thiếu ảnh vẫn trả được, chỉ nhắc lại cho quản lý.
      if (kq?.warning) toast.warning(kq.warning);
      await fetchPayroll();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Cannot mark paid', error);
      toast.error(error?.message || 'Không thể đánh dấu đã thanh toán');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusSteps = () => {
    const steps = [
      { key: 'DRAFT', label: 'Nháp', completed: false },
      { key: 'PENDING_APPROVAL', label: 'Chờ duyệt', completed: false },
      { key: 'APPROVED', label: 'Đã duyệt', completed: false },
      { key: 'CONFIRMED', label: 'NV đã xác nhận', completed: false },
      { key: 'PAID', label: 'Đã thanh toán', completed: false },
    ];

    const currentStatus = payroll?.status;
    const statusOrder = ['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'CONFIRMED', 'PAID'];
    const currentIndex = statusOrder.indexOf(currentStatus);

    steps.forEach((step, index) => {
      step.completed = index <= currentIndex;
    });

    return steps;
  };

  const canApprove = payroll?.status === 'DRAFT' || payroll?.status === 'PENDING_APPROVAL';
  // #175506 — chỉ trả lương khi nhân viên đã xác nhận. Ảnh chuyển khoản KHÔNG phải điều kiện
  // (bổ sung sếp `ul_anh_canhbao`): thiếu ảnh chỉ cảnh báo, nút vẫn bấm được.
  const evidenceUrls = payroll?.payment_evidence_urls || [];
  const canMarkPaid = payroll?.status === 'CONFIRMED';
  // Kỳ lương coi là ĐÃ CHỐT khi nhân viên đã xác nhận — không đổi khoản ứng sau lưng họ.
  const advanceLocked = payroll?.status === 'CONFIRMED' || payroll?.status === 'PAID';
  const advanceTotal = advances.reduce((sum, a) => sum + Number(a.amount || 0), 0);

  const handleEditHourlyRate = () => {
    if (payroll?.hourly_rate) {
      // #154220: giữ NGUYÊN giá trị đang có, kể cả khi lệch bậc (21.000/23.000 của dữ liệu cũ) —
      // không tự nhảy về bậc gần nhất, để không âm thầm đổi lương thật của nhân viên.
      setNewHourlyRate(Number(payroll.hourly_rate));
      setShowEditHourlyRateModal(true);
    }
  };

  const handleUpdateHourlyRate = async () => {
    if (!payroll?.employee_id) {
      toast.error('Không xác định được nhân viên');
      return;
    }

    if (!isHourlyRateTier(newHourlyRate)) {
      toast.error(
        `Lương giờ chỉ được chọn một trong các bậc: ${HOURLY_RATE_TIERS.map(formatHourlyRateTier).join(', ')}`,
      );
      return;
    }

    try {
      setUpdatingHourlyRate(true);
      await employeeAPI.updateEmployee(payroll.employee_id, {
        hourly_rate: Number(newHourlyRate),
      });

      toast.success('Đã cập nhật lương giờ thành công');
      
      // Tính lại payroll sau khi cập nhật hourly_rate
      try {
        await payrollAPI.calculate({
          employee_id: payroll.employee_id,
          month: payroll.month,
        });
        toast.info('Đã tính lại bảng lương với lương giờ mới');
      } catch (calcError) {
        console.error('Error recalculating payroll:', calcError);
        toast.warning('Đã cập nhật lương giờ nhưng không thể tính lại bảng lương tự động');
      }

      setShowEditHourlyRateModal(false);
      await fetchPayroll();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Cannot update hourly rate', error);
      toast.error(error?.message || 'Không thể cập nhật lương giờ');
    } finally {
      setUpdatingHourlyRate(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Chi tiết bảng lương</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          )}

          {!loading && !payroll && <div className="text-muted">Không có dữ liệu</div>}

          {!loading && payroll && (
            <>
              <Card className="mb-3">
                <Card.Body>
                  <h5 className="mb-3">Thông tin nhân viên</h5>
                  <Row>
                    <Col md={6}>
                      <div className="mb-2">
                        <strong>Tên:</strong> {payroll.employee?.full_name || payroll.employee?.username || '-'}
                      </div>
                      <div className="mb-2">
                        <strong>Username:</strong> {payroll.employee?.username || '-'}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2">
                        <strong>Tháng:</strong> {formatMonth(payroll.month)}
                      </div>
                      <div className="mb-2 d-flex align-items-center gap-2">
                        <div>
                          <strong>Lương giờ:</strong> {formatCurrency(payroll.hourly_rate)}/h
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleEditHourlyRate}
                          disabled={payroll.status === 'APPROVED' || payroll.status === 'PAID'}
                          title="Chỉnh sửa lương giờ"
                        >
                          <i className="material-icons" style={{ fontSize: '16px' }}>
                            edit
                          </i>
                        </Button>
                      </div>
                      {(payroll.status === 'APPROVED' || payroll.status === 'PAID') && (
                        <div className="small text-muted">
                          ⚠️ Không thể chỉnh sửa lương giờ khi bảng lương đã {payroll.status === 'APPROVED' ? 'duyệt' : 'thanh toán'}
                        </div>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Body>
                  <h5 className="mb-3">Tóm tắt lương</h5>
                  <div className="mb-2">
                    <div className="d-flex justify-content-between">
                      <span>Lương từ giờ:</span>
                      <strong>{formatCurrency(payroll.salary_from_hours)}</strong>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="d-flex justify-content-between">
                      <span>Thưởng / Phạt:</span>
                      <strong className={payroll.net_reward_amount >= 0 ? 'text-success' : 'text-danger'}>
                        {payroll.net_reward_amount >= 0 ? '+' : ''}
                        {formatCurrency(payroll.net_reward_amount)}
                      </strong>
                    </div>
                  </div>
                  {advanceTotal > 0 && (
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <span>Đã ứng lương:</span>
                        <strong className="text-danger">-{formatCurrency(advanceTotal)}</strong>
                      </div>
                    </div>
                  )}
                  <hr />
                  <div className="mb-3">
                    <div className="d-flex justify-content-between">
                      <h5>TỔNG LƯƠNG:</h5>
                      <h5 className="text-primary">{formatCurrency(payroll.final_salary)}</h5>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" size="sm" onClick={() => setShowHoursModal(true)}>
                      Xem chi tiết giờ
                    </Button>
                    <Button variant="outline-primary" size="sm" onClick={() => setShowPointsModal(true)}>
                      Xem chi tiết điểm
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Body>
                  <h5 className="mb-3">Chi tiết giờ làm việc</h5>
                  <Row>
                    <Col md={4}>
                      <div className="mb-2">
                        <strong>Giờ thường:</strong> {payroll.total_normal_hours || 0}h
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-2">
                        <strong>Giờ đêm:</strong> {payroll.total_overnight_hours || 0}h
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-2">
                        <strong>Giờ lễ:</strong> {payroll.total_holiday_hours || 0}h
                      </div>
                    </Col>
                  </Row>
                  <div className="mt-2">
                    <strong>Giờ quy đổi tổng:</strong> {payroll.total_converted_hours || 0}h
                  </div>
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Chi tiết điểm</h5>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowManualPointModal(true)}
                      disabled={payroll.status === 'APPROVED' || payroll.status === 'PAID'}
                    >
                      + Thêm/Trừ điểm
                    </Button>
                  </div>
                  <Row>
                    <Col md={4}>
                      <div className="mb-2">
                        <strong>Điểm thưởng dùng (80%):</strong> {payroll.reward_points_used || 0}
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-2">
                        <strong>Điểm thưởng tích lũy (20%):</strong> {payroll.reward_points_accumulated || 0}
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-2">
                        <strong>Điểm phạt:</strong> {payroll.penalty_points || 0}
                      </div>
                    </Col>
                  </Row>
                  {(payroll.status === 'APPROVED' || payroll.status === 'PAID') && (
                    <div className="mt-2 small text-muted">
                      ⚠️ Bảng lương đã {payroll.status === 'APPROVED' ? 'duyệt' : 'thanh toán'}, không thể thêm điểm
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* #175506 — Ứng lương của đúng kỳ lương này */}
              <Card className="mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Ứng lương</h5>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowAdvanceForm((v) => !v)}
                      disabled={advanceLocked || savingAdvance}
                    >
                      + Thêm khoản ứng
                    </Button>
                  </div>

                  {advanceQuota?.computable && (
                    <div className="small text-muted mb-2">
                      Trần ứng kỳ này: <strong>{formatCurrency(advanceQuota.limit)}</strong>{' '}
                      ({advanceQuota.max_percent}% lương thực nhận {formatCurrency(advanceQuota.gross_salary)})
                      {' · '}đã ứng <strong>{formatCurrency(advanceQuota.total_advanced)}</strong>
                      {' · '}còn ứng được <strong>{formatCurrency(advanceQuota.remaining)}</strong>
                    </div>
                  )}
                  {advanceQuota?.warning && (
                    <div className="alert alert-warning py-2 px-3 small mb-2">⚠️ {advanceQuota.warning}</div>
                  )}

                  {advances.length === 0 && !showAdvanceForm && (
                    <div className="text-muted small">Kỳ lương này chưa có khoản ứng nào.</div>
                  )}

                  {advances.map((advance) => (
                    <div
                      key={advance.id}
                      className="d-flex justify-content-between align-items-center border-bottom py-2"
                    >
                      <div>
                        <strong className="text-danger">-{formatCurrency(advance.amount)}</strong>
                        {advance.note && <span className="ms-2 text-muted small">{advance.note}</span>}
                        <div className="text-muted small">
                          {advance.creator?.full_name || 'Không rõ'} · {formatDateTime(advance.created_at)}
                        </div>
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteAdvance(advance.id)}
                        disabled={advanceLocked || savingAdvance}
                      >
                        Xoá
                      </Button>
                    </div>
                  ))}

                  {showAdvanceForm && (
                    <div className="mt-3">
                      <Row>
                        <Col md={5}>
                          <Form.Group className="mb-2">
                            <Form.Label>Số tiền ứng (VND)</Form.Label>
                            <Form.Control
                              type="number"
                              min={0}
                              step={1000}
                              value={advanceAmount}
                              onChange={(e) => setAdvanceAmount(e.target.value)}
                              placeholder="VD: 500000"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={7}>
                          <Form.Group className="mb-2">
                            <Form.Label>Ghi chú</Form.Label>
                            <Form.Control
                              type="text"
                              maxLength={255}
                              value={advanceNote}
                              onChange={(e) => setAdvanceNote(e.target.value)}
                              placeholder="Không bắt buộc"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Button variant="primary" size="sm" onClick={handleCreateAdvance} disabled={savingAdvance}>
                        {savingAdvance ? <Spinner size="sm" /> : 'Lưu khoản ứng'}
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => setShowAdvanceForm(false)}
                        disabled={savingAdvance}
                      >
                        Huỷ
                      </Button>
                    </div>
                  )}

                  {advanceLocked && (
                    <div className="mt-2 small text-muted">
                      ⚠️ Nhân viên đã xác nhận bảng lương, không thể thay đổi khoản ứng
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* #175506 — Ảnh chuyển khoản: KHÔNG bắt buộc, thiếu ảnh chỉ cảnh báo */}
              <Card className="mb-3">
                <Card.Body>
                  <h5 className="mb-3">Ảnh chuyển khoản</h5>
                  {payroll.paid_without_evidence && (
                    <div className="alert alert-warning py-2 px-3 small mb-2">
                      ⚠️ Phiếu này đã đánh dấu thanh toán khi CHƯA có ảnh chuyển khoản
                      {evidenceUrls.length > 0 ? ' (ảnh dưới đây là bổ sung sau).' : ' — nên bổ sung ảnh để đối chiếu.'}
                    </div>
                  )}
                  {evidenceUrls.length === 0 && (
                    <div className="text-muted small mb-2">
                      Chưa có ảnh. Không bắt buộc — vẫn đánh dấu đã thanh toán được, đính ảnh khi có.
                    </div>
                  )}
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {evidenceUrls.map((url) => (
                      <div key={url} className="text-center">
                        <a href={url} target="_blank" rel="noreferrer">
                          <img
                            src={url}
                            alt="Ảnh chuyển khoản"
                            style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 6 }}
                          />
                        </a>
                        {payroll.status !== 'PAID' && (
                          <div>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0"
                              onClick={() => handleRemoveEvidence(url)}
                              disabled={uploadingEvidence}
                            >
                              Xoá
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Đính ảnh được ở MỌI trạng thái, kể cả phiếu đã trả — phiếu trả khi chưa có
                      ảnh vẫn phải bổ sung được chứng từ sau (máy chủ chỉ cấm GỠ ảnh đã có). */}
                  <Form.Group>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      size="sm"
                      onChange={handleUploadEvidence}
                      disabled={uploadingEvidence}
                    />
                    {uploadingEvidence && (
                      <div className="small text-muted mt-1">
                        <Spinner size="sm" /> Đang tải ảnh...
                      </div>
                    )}
                  </Form.Group>
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Body>
                  <h5 className="mb-3">Quy trình duyệt</h5>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    {getStatusSteps().map((step, index) => (
                      <React.Fragment key={step.key}>
                        <div className="text-center flex-fill">
                          <div
                            className={`rounded-circle d-inline-flex align-items-center justify-content-center ${
                              step.completed ? 'bg-primary text-white' : 'bg-light text-muted'
                            }`}
                            style={{ width: '40px', height: '40px' }}
                          >
                            {index + 1}
                          </div>
                          <div className="mt-2 small">{step.label}</div>
                        </div>
                        {index < getStatusSteps().length - 1 && (
                          <div
                            className={`flex-fill mx-2 ${
                              step.completed ? 'border-top border-primary' : 'border-top border-light'
                            }`}
                            style={{ height: '2px' }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                  {payroll.approved_by && (
                    <div className="mb-2">
                      <strong>Duyệt bởi:</strong> {payroll.approved_by}
                    </div>
                  )}
                  {payroll.approved_at && (
                    <div className="mb-2">
                      <strong>Duyệt lúc:</strong> {formatDateTime(payroll.approved_at)}
                    </div>
                  )}
                  {payroll.confirmed_at && (
                    <div className="mb-2">
                      <strong>Nhân viên xác nhận lúc:</strong> {formatDateTime(payroll.confirmed_at)}
                    </div>
                  )}
                  {payroll.paid_at && (
                    <div className="mb-2">
                      <strong>Thanh toán lúc:</strong> {formatDateTime(payroll.paid_at)}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Đóng
          </Button>
          {canApprove && (
            <Button variant="primary" onClick={handleApprove} disabled={actionLoading}>
              {actionLoading ? <Spinner size="sm" /> : 'Duyệt'}
            </Button>
          )}
          {payroll?.status === 'CONFIRMED' && evidenceUrls.length === 0 && (
            <span className="text-warning small me-2">
              ⚠️ Chưa có ảnh chuyển khoản — vẫn đánh dấu được, nhớ bổ sung ảnh sau
            </span>
          )}
          {canMarkPaid && (
            <Button variant="success" onClick={handleMarkPaid} disabled={actionLoading}>
              {actionLoading ? <Spinner size="sm" /> : 'Đánh dấu đã thanh toán'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {showHoursModal && payrollId && (
        <HoursDetailModal
          show={showHoursModal}
          onHide={() => setShowHoursModal(false)}
          payrollId={payrollId}
        />
      )}

      {showPointsModal && payrollId && (
        <PointsDetailModal
          show={showPointsModal}
          onHide={() => setShowPointsModal(false)}
          payrollId={payrollId}
        />
      )}

      {showManualPointModal && payroll && (
        <ManualPointModal
          show={showManualPointModal}
          onHide={() => setShowManualPointModal(false)}
          employeeId={payroll.employee_id}
          employeeName={payroll.employee?.full_name || payroll.employee?.username}
          currentMonth={payroll.month}
          payrollStatus={payroll.status}
          onSuccess={() => {
            fetchPayroll();
            if (onRefresh) onRefresh();
          }}
        />
      )}

      {showEditHourlyRateModal && payroll && (
        <Modal show={showEditHourlyRateModal} onHide={() => setShowEditHourlyRateModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Chỉnh sửa lương giờ</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <strong>Nhân viên:</strong> {payroll.employee?.full_name || payroll.employee?.username || '-'}
            </div>
            <div className="mb-3">
              <strong>Lương giờ hiện tại:</strong> {formatCurrency(payroll.hourly_rate)}/h
            </div>
            <Form.Group>
              <Form.Label>Bậc lương giờ mới (VND/giờ) <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={newHourlyRate}
                onChange={(e) => setNewHourlyRate(Number(e.target.value))}
              >
                {/* Lương cũ lệch bậc vẫn hiện đúng số của nó, chọn không được — xem #154220 */}
                {!isHourlyRateTier(newHourlyRate) && (
                  <option value={newHourlyRate} disabled>
                    {formatHourlyRateTier(newHourlyRate)} (ngoài bậc — chọn bậc mới bên dưới)
                  </option>
                )}
                {HOURLY_RATE_TIERS.map((tier) => (
                  <option key={tier} value={tier}>
                    {formatHourlyRateTier(tier)}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Chỉ có {HOURLY_RATE_TIERS.length} bậc: {HOURLY_RATE_TIERS.map(formatHourlyRateTier).join(' · ')}
              </Form.Text>
            </Form.Group>
            <div className="alert alert-info mt-3 mb-0">
              <strong>Lưu ý:</strong> Sau khi cập nhật, hệ thống sẽ tự động tính lại bảng lương cho tháng {formatMonth(payroll.month)}.
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditHourlyRateModal(false)} disabled={updatingHourlyRate}>
              Hủy
            </Button>
            <Button variant="primary" onClick={handleUpdateHourlyRate} disabled={updatingHourlyRate}>
              {updatingHourlyRate ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Đang cập nhật...
                </>
              ) : (
                'Cập nhật'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

